import { Box, useColorModeValue, useToast } from "@chakra-ui/react";
import React from "react";
import { QuoteApi } from "../api/QuoteApi";
import ComboBox from "../components/form-fields/ComboBox";
import QuoteTableContainer from "../components/QuoteTableContainer";
import useUser from "../hooks/useUser";
import { Quote } from "../lib/models/quote.model";
import { Broker } from "../lib/models/sf-user-profile.model";
import { QuoteUtils } from "../lib/quote-utils";
import { SelectItem } from "../lib/types/SelectItem";

function BrokerQuotesView() {
  const toast = useToast();
  const { sfProfile } = useUser();

  const [quotes, setQuotes] = React.useState<Quote[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [activeBroker, setActiveBroker] = React.useState<Broker>();
  const toastVariant = useColorModeValue("subtle", "solid");

  const brokerItems: SelectItem[] = React.useMemo(() => {
    if (sfProfile?.brokers) {
      return sfProfile?.brokers.map((broker) => ({
        label: broker.name,
        value: broker.accountid,
      }));
    }
    return [];
  }, [sfProfile?.brokers]);

  const [selectedBrokerItem, setSelectedBrokerItem] =
    React.useState<SelectItem | null>(brokerItems[0]);

  React.useEffect(() => {
    if (!sfProfile?.brokers) {
      return;
    }
    setActiveBroker(sfProfile.brokers[0]);
  }, [sfProfile]);

  const handleBrokerSelect = (brokerItem: SelectItem | null) => {
    const selectBroker = sfProfile?.brokers.find(
      (broker) => broker.accountid === brokerItem?.value
    );

    setSelectedBrokerItem(brokerItem);
    setActiveBroker(selectBroker);
  };

  const fetchQuotes = React.useCallback(() => {
    setLoading(true);

    if (!activeBroker) {
      return;
    }

    QuoteApi.getQuotesForReviewById(activeBroker?.accountid)
      .then((res) => {
        const activeQuotes = res.data.map((x) => {
          x.quoteTypeLabel = QuoteUtils.PrettyPrintQuoteType(x.quoteType);
          x.createdBy = `${x.firstname} ${x.lastname}`;
          return x;
        });

        setQuotes(activeQuotes);
      })
      .catch((err) => {
        toast({
          title: "Unable to Load Quotes",
          description: "An error has occurred when attempting to fetch quotes",
          status: "error",
          variant: toastVariant,
          isClosable: true,
          position: "top-right",
        });
      })
      .finally(() => setLoading(false));
  }, [setQuotes, activeBroker, toast, toastVariant]);

  React.useEffect(() => {
    if (!activeBroker) {
      return;
    }
    fetchQuotes();
  }, [activeBroker, fetchQuotes]);

  return (
    <QuoteTableContainer
      refetchQuotes={fetchQuotes}
      loading={loading}
      title={
        activeBroker
          ? `Quotes for ${activeBroker.name}`
          : "No Name Provided for Broker"
      }
      quotes={quotes}
      emptyTableLabel={
        activeBroker?.name
          ? `There are no quotes for ${activeBroker.name}.`
          : "Select a broker to see their quotes."
      }
      showAddQuoteMenu={false}
    >
      <Box width={330} mb={4}>
        <ComboBox
          items={brokerItems}
          label=""
          onSelect={handleBrokerSelect}
          isDisabled={loading || !!selectedBrokerItem}
          itemSelected={selectedBrokerItem}
          placeholder="Select a Broker..."
        />
      </Box>
    </QuoteTableContainer>
  );
}

export default BrokerQuotesView;
